import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Button, IconButton } from './primitives/Button.js';
import { useTurnstile } from '@dedupr/turnstile';
import './AskDedupr.css';

export interface AskDeduprProps {
  open: boolean;
  onToggle: () => void;
  onGoDocs: () => void;
  onOpenBooking: () => void;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

type Mode = 'chat' | 'lead' | 'handoff';
type LeadPurpose = 'book' | 'message';

const PROMPTS = ['Can you read my accounting tool?', 'How long is rollback?'];
const GENERIC_ERROR = "Sorry — I couldn't reach the assistant just now. Please try again, or open the documentation.";
const HANDOFF_POLL_MS = 4000;
const HANDOFF_BUSY_MS = 3 * 60 * 1000;

/** The fixed help FAB + chat popover, present on every screen. */
export function AskDedupr({ open, onToggle, onGoDocs, onOpenBooking }: AskDeduprProps) {
  const [draft, setDraft] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [mode, setMode] = useState<Mode>('chat');
  const [leadPurpose, setLeadPurpose] = useState<LeadPurpose>('book');
  const turnstile = useTurnstile();
  const bodyRef = useRef<HTMLDivElement>(null);

  // Lead capture (booking or "leave a message" — same form, different copy/email subject via `leadPurpose`).
  const [leadName, setLeadName] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [leadTime, setLeadTime] = useState('');
  const [leadStatus, setLeadStatus] = useState<'idle' | 'submitting' | 'error'>('idle');
  const [leadError, setLeadError] = useState('');

  // Human handoff.
  const [handoffBusy, setHandoffBusy] = useState(false);
  const sessionIdRef = useRef<string | null>(null);
  const handoffMessageCountRef = useRef(0);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const busyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      if (busyTimerRef.current) clearTimeout(busyTimerRef.current);
    };
  }, []);

  function scrollToEnd() {
    requestAnimationFrame(() => {
      const body = bodyRef.current;
      // jsdom (used by the test suite) doesn't implement Element.scrollTo —
      // guard it rather than crash the async RAF callback in CI.
      if (body && typeof body.scrollTo === 'function') {
        body.scrollTo({ top: body.scrollHeight });
      }
    });
  }

  function appendAssistant(content: string) {
    setMessages((current) => [...current, { role: 'assistant', content }]);
    scrollToEnd();
  }

  async function sendMessage(text: string) {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    const history = [...messages, { role: 'user' as const, content: trimmed }];
    setMessages(history);
    setDraft('');
    setSending(true);
    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: history, turnstileToken: turnstile.token }),
      });
      const data = (await response.json().catch(() => null)) as { ok?: boolean; reply?: string; error?: string } | null;
      // Single-use token — always get a fresh one queued for the next message.
      turnstile.reset();
      if (!response.ok || !data?.ok || !data.reply) {
        appendAssistant(data?.error || GENERIC_ERROR);
        return;
      }
      appendAssistant(data.reply);
    } catch {
      turnstile.reset();
      appendAssistant(GENERIC_ERROR);
    } finally {
      setSending(false);
      scrollToEnd();
    }
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    void sendMessage(draft);
  }

  function openLeadForm(purpose: LeadPurpose) {
    setLeadPurpose(purpose);
    setLeadStatus('idle');
    setLeadError('');
    setMode('lead');
  }

  async function submitLead(event: FormEvent) {
    event.preventDefault();
    setLeadStatus('submitting');
    setLeadError('');
    try {
      const response = await fetch('/api/chat-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: leadName,
          email: leadEmail,
          preferredTime: leadTime,
          transcript: messages,
          turnstileToken: turnstile.token,
        }),
      });
      const data = (await response.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
      turnstile.reset();
      if (!response.ok || !data?.ok) {
        setLeadError(data?.error || 'Could not send your details right now. Please try again shortly.');
        setLeadStatus('error');
        return;
      }
      appendAssistant(
        leadPurpose === 'book'
          ? `Thanks, ${leadName} — I've sent your details. Pick a time on the calendar that just opened.`
          : `Thanks, ${leadName} — I've passed your message along. We'll reply at ${leadEmail}.`
      );
      setMode('chat');
      setLeadName('');
      setLeadEmail('');
      setLeadTime('');
      if (leadPurpose === 'book') onOpenBooking();
    } catch {
      turnstile.reset();
      setLeadError('Could not reach the server. Check your connection and try again.');
      setLeadStatus('error');
    }
  }

  async function pollHandoff() {
    const sessionId = sessionIdRef.current;
    if (!sessionId) return;
    try {
      const response = await fetch(`/api/chat-handoff/${sessionId}/poll`);
      const data = (await response.json().catch(() => null)) as { ok?: boolean; messages?: Array<{ content: string }> } | null;
      if (!data?.ok || !data.messages) return;
      const newOnes = data.messages.slice(handoffMessageCountRef.current);
      if (newOnes.length > 0) {
        handoffMessageCountRef.current = data.messages.length;
        // A person answered — the 3-minute "everyone's busy" notice no longer applies.
        if (busyTimerRef.current) {
          clearTimeout(busyTimerRef.current);
          busyTimerRef.current = null;
        }
        for (const m of newOnes) appendAssistant(m.content);
      }
    } catch {
      // A missed poll just retries on the next tick — no need to surface it.
    }
  }

  async function startHandoff() {
    const sessionId = crypto.randomUUID();
    sessionIdRef.current = sessionId;
    handoffMessageCountRef.current = 0;
    setHandoffBusy(false);
    setMode('handoff');
    appendAssistant('Connecting you to a person — hang tight, this can take a few minutes.');

    try {
      const response = await fetch('/api/chat-handoff/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, transcript: messages, turnstileToken: turnstile.token }),
      });
      const data = (await response.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
      turnstile.reset();
      if (!response.ok || !data?.ok) {
        appendAssistant(data?.error || GENERIC_ERROR);
        setMode('chat');
        sessionIdRef.current = null;
        return;
      }
    } catch {
      turnstile.reset();
      appendAssistant(GENERIC_ERROR);
      setMode('chat');
      sessionIdRef.current = null;
      return;
    }

    pollTimerRef.current = setInterval(() => void pollHandoff(), HANDOFF_POLL_MS);
    busyTimerRef.current = setTimeout(() => {
      setHandoffBusy(true);
      appendAssistant("All our humans are already busy right now. Want to leave a message, or book a meeting instead?");
    }, HANDOFF_BUSY_MS);
  }

  const showQuickActions = mode === 'chat';

  return (
    <div className="land-ask">
      {open && (
        <div className="land-ask__panel" role="dialog" aria-label="Ask Dedupr">
          <div className="land-ask__header">
            <span className="land-ask__avatar">✻</span>
            <div className="land-ask__title">
              <div className="land-ask__title-main">Ask Dedupr</div>
              <div className="land-ask__title-sub">Online · answers about the product</div>
            </div>
            <IconButton size={26} aria-label="Close help" onClick={onToggle}>
              ✕
            </IconButton>
          </div>
          <div className="land-ask__body" ref={bodyRef}>
            <div className="land-ask__bubble">
              Hi — ask me about connectors, matching tiers, rollback windows, or pricing. You can also
              ask to book a meeting or chat with a person.
            </div>
            {messages.map((message, index) => (
              <div
                key={index}
                className={`land-ask__bubble${message.role === 'user' ? ' land-ask__bubble--user' : ''}`}
              >
                {message.content}
              </div>
            ))}
            {sending && <div className="land-ask__bubble land-ask__bubble--pending">Thinking…</div>}
            {messages.length === 0 && mode === 'chat' && (
              <div className="land-ask__chips">
                {PROMPTS.map((prompt) => (
                  <Button key={prompt} variant="tint" size="xs" onClick={() => sendMessage(prompt)}>
                    {prompt}
                  </Button>
                ))}
                <Button variant="secondary" size="xs" onClick={onGoDocs}>
                  Open documentation ↗
                </Button>
              </div>
            )}
            {mode === 'handoff' && handoffBusy && (
              <div className="land-ask__chips">
                <Button variant="tint" size="xs" onClick={() => openLeadForm('message')}>
                  Leave a message
                </Button>
                <Button variant="tint" size="xs" onClick={() => openLeadForm('book')}>
                  Book a meeting
                </Button>
              </div>
            )}
          </div>

          {mode === 'lead' ? (
            <form className="land-ask__lead" onSubmit={submitLead}>
              <div className="land-ask__lead-title">
                {leadPurpose === 'book' ? 'Book a meeting' : 'Leave a message'}
              </div>
              <input
                placeholder="Name"
                value={leadName}
                onChange={(event) => setLeadName(event.target.value)}
                required
                disabled={leadStatus === 'submitting'}
              />
              <input
                type="email"
                placeholder="Email"
                value={leadEmail}
                onChange={(event) => setLeadEmail(event.target.value)}
                required
                disabled={leadStatus === 'submitting'}
              />
              {leadPurpose === 'book' && (
                <input
                  placeholder="Preferred time (optional)"
                  value={leadTime}
                  onChange={(event) => setLeadTime(event.target.value)}
                  disabled={leadStatus === 'submitting'}
                />
              )}
              {leadStatus === 'error' && <div className="land-ask__turnstile-error">{leadError}</div>}
              <div ref={turnstile.containerRef} className="land-ask__turnstile" />
              <div className="land-ask__lead-actions">
                <Button variant="secondary" size="xs" type="button" onClick={() => setMode('chat')}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="xs"
                  type="submit"
                  disabled={leadStatus === 'submitting' || (turnstile.enabled && !turnstile.token)}
                >
                  {leadStatus === 'submitting' ? 'Sending…' : 'Send'}
                </Button>
              </div>
            </form>
          ) : (
            <>
              <div ref={turnstile.containerRef} className="land-ask__turnstile" />
              {turnstile.error && <div className="land-ask__turnstile-error">{turnstile.error}</div>}
              {showQuickActions && (
                <div className="land-ask__quick-actions">
                  <button type="button" onClick={() => openLeadForm('book')}>
                    📅 Book a meeting
                  </button>
                  <button type="button" onClick={() => void startHandoff()}>
                    🧑 Chat with a person
                  </button>
                </div>
              )}
              <form className="land-ask__composer" onSubmit={handleSubmit}>
                <input
                  placeholder="Ask a question…"
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  disabled={sending}
                />
                <IconButton
                  type="submit"
                  size={36}
                  className="land-ask__send"
                  aria-label="Send"
                  disabled={sending || !draft.trim() || (turnstile.enabled && !turnstile.token)}
                >
                  ↑
                </IconButton>
              </form>
            </>
          )}
        </div>
      )}
      <div className="land-ask__fab-row">
        <span className="land-ask__fab-label">Ask Dedupr — help agent</span>
        <button
          className="land-ask__fab"
          aria-label="Ask Dedupr — help agent"
          title="Ask Dedupr — help agent"
          onClick={onToggle}
        >
          <span className="land-ask__fab-glyph">✻</span>
          {/* data-anim3d is what the reduced-motion guard in styles/tokens.css
              keys on; without it this dot keeps pulsing for a user who asked
              for no motion. */}
          <span className="land-ask__fab-status" data-anim3d="1" />
        </button>
      </div>
    </div>
  );
}
